import moment from 'moment';
import { buildLogicalNameToFieldKeyMap, getTemplateFieldValue } from './logicalNameToFieldKey';
import { unwrapDoubleWrappedParagraph } from '../../../../common/normalizers/richText';
import {
  WORKKARD_FIELD_KEY,
  MAILING_SAME_AS_ABOVE_FIELD_KEY,
  PROPERTY_LOCATION_PREFIX,
  MAILING_ADDRESS_PREFIX,
} from '../constants/fieldKeys';

const DATE_FIELD_TYPES = new Set([
  'date',
  'datetime',
  'datetimetimezone',
  'datewithusertimezone',
  'datewithtimezone',
]);

const DATE_INPUT_FORMATS = ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601];

function getFieldType(field) {
  return String(field?.fieldType ?? field?.FieldType ?? '').toLowerCase();
}

function isDateFieldType(fieldType) {
  return DATE_FIELD_TYPES.has(fieldType);
}

function toDateDisplayString(value) {
  if (value === null || value === undefined || value === '') return '';

  // Number: Unix seconds (or ms) since epoch.
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value <= 0) return '';
    const seconds = value > 1e12 ? value / 1000 : value;
    const m = moment.unix(seconds);
    return m.isValid() ? m.format('MM/DD/YYYY') : '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    // Numeric string: treat as Unix seconds/ms.
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (asNum > 0) {
        const seconds = asNum > 1e12 ? asNum / 1000 : asNum;
        const m = moment.unix(seconds);
        if (m.isValid()) return m.format('MM/DD/YYYY');
      }
      return '';
    }

    // Regular date-shaped string (already MM/DD/YYYY, ISO, etc.).
    const strictParsed = moment(trimmed, DATE_INPUT_FORMATS, true);
    if (strictParsed.isValid()) return strictParsed.format('MM/DD/YYYY');

    const looseParsed = moment(trimmed);
    return looseParsed.isValid() ? looseParsed.format('MM/DD/YYYY') : '';
  }

  return '';
}

function coerceIncomingValue(field, value) {
  const fieldType = getFieldType(field);

  if (isDateFieldType(fieldType)) {
    return toDateDisplayString(value);
  }

  if (fieldType === 'checkbox' || fieldType === 'boolean') {
    if (value === true || value === 'Yes' || value === 'yes' || value === 1 || value === '1') return true;
    if (value === false || value === 'No' || value === 'no' || value === 0 || value === '0') return false;
    return Boolean(value);
  }

  if (fieldType === 'multiplechoicelist') {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string' && value.length > 0) {
      return value.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
  }

  if (fieldType === 'file') {
    return Array.isArray(value) ? value : [];
  }

  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
}

function normalizeAddressPart(value) {
  return String(value ?? '').trim().toLowerCase();
}

function addressesMatch(initialValues) {
  const propertyKeys = Object.keys(initialValues).filter((key) => key.startsWith(PROPERTY_LOCATION_PREFIX));
  if (propertyKeys.length === 0) return false;

  return propertyKeys.every((key) => {
    const mailingKey = key.replace(PROPERTY_LOCATION_PREFIX, MAILING_ADDRESS_PREFIX);
    return normalizeAddressPart(initialValues[key]) === normalizeAddressPart(initialValues[mailingKey]);
  });
}

export function buildEditInitialValues(workkardData, fields = []) {
  if (!workkardData) return {};

  const initialValues = {};

  initialValues[WORKKARD_FIELD_KEY.NUMBER] = workkardData.workkard_number || '';
  initialValues[WORKKARD_FIELD_KEY.TITLE] = workkardData.workkard_title || '';
  initialValues[WORKKARD_FIELD_KEY.DESCRIPTION] = unwrapDoubleWrappedParagraph(workkardData.workkard_summary || '');
  if (workkardData.carrier_id) {
    initialValues[WORKKARD_FIELD_KEY.CARRIER] = String(workkardData.carrier_id);
  }

  if (workkardData.sponsor?.user_id) {
    initialValues[WORKKARD_FIELD_KEY.OWNER] = String(workkardData.sponsor.user_id);
  }

  const logicalNameMap = buildLogicalNameToFieldKeyMap(fields);
  const templateFields = workkardData.template_fields || {};

  fields.forEach((field) => {
    const fieldKey = field.fieldKey || field.FieldKey;
    if (!fieldKey) return;

    const value = getTemplateFieldValue(templateFields, fieldKey, logicalNameMap);
    if (value === null || value === undefined) return;

    initialValues[fieldKey] = coerceIncomingValue(field, value);
  });

  initialValues[MAILING_SAME_AS_ABOVE_FIELD_KEY] = addressesMatch(initialValues);

  if (workkardData.WorkkardFnolFile && Array.isArray(workkardData.WorkkardFnolFile)) {
    initialValues.attachfiles_file = workkardData.WorkkardFnolFile.map((file) => ({
      file_id: file.file_id,
      name: file.file_name,
      guid: file.guid,
      url: file.file_url,
      caption: file.caption,
      file_type: file.file_type,
      size: file.file_size,
    }));
  }

  return initialValues;
}
